
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";

import { Alert, Dialog, Table } from "../../layout";

import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";

import { AppContext } from "../../App";
import { DropLi } from "../../hooks";
import { safeOnComplete } from "../../utils/safeOnComplete";

import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable User Details Content
//------------------------------------------------
const UserDetailsContent = ({ user, t }) => {
  if (!user) return null;

  const details = [
    [t("firstName"), user.firstName],
    [t("lastName"), user.lastName],
    [t("userName"), user.userName],
    [t("dateOfBirth"), user.dob],
    [t("gender"), user.gender],
    [t("phone"), user.phone],
    [t("userRole"), user.role],
    [t("activeStatus"), user.isActive],
    [t("address"), user.address],
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("userDetails")}
      </Typography>

      <TableContainer>
        <MuiTable size="small">
          <TableBody>
            {details.map(([label, value], index) => (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    width: "40%",
                    fontWeight: "bold",
                  }}
                >
                  {label}
                </TableCell>

                <TableCell>
                  {value ?? ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

//------------------------------------------------
// Users
//------------------------------------------------
const Users = (props) => {
  const { t } = useTranslation();

  const {
    _list,
    _getById,
    _clear,
    _delete,
    _type,
    _store,
  } = props;

  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const { Gender, Role, BoolType } = DropLi();

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showView, setShowView] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Users by Store
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.User + controller.GetByStore,
        _store.id,
        actions.ActionType.List,
        () => setLoader(false)
      );
    }

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Lookup Display Values
  //------------------------------------------------
  const getLookupName = (list, value) => {
    return (
      (Array.isArray(list) ? list : []).find(
        (item) =>
          String(item.id) === String(value)
      )?.name || ""
    );
  };

  //------------------------------------------------
  // Users Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    if (
      _type !== "users" ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => ({
      ...obj,

      gender: getLookupName(Gender, obj.gender),

      role: getLookupName(Role, obj.role),

      isActive: getLookupName(
        BoolType,
        obj.isActive
      ),
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_list, _type, Gender, Role, BoolType]);

  //------------------------------------------------
  // Add / Edit User
  //------------------------------------------------
  const handleAddEdit = (user = null) => {
    navigate(
      user?.id
        ? `user-form/${user.id}`
        : "user-form"
    );
  };

  //------------------------------------------------
  // View User Details
  //------------------------------------------------
  const handleView = (data) => {
    const selected = tableData.find(
      (user) =>
        String(user.id) === String(data.id)
    );

    setSelectedUser(selected || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setSelectedUser(null);
  };

  //------------------------------------------------
  // Delete User
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteUserConfirmation"),

      onConfirm: () => {
        setLoader(true);

        const onComplete = safeOnComplete(
          (res) => {
            setLoader(false);

            setAlert({
              isOpen: true,
              type:
                res?.status === 200
                  ? "success"
                  : "error",
              title:
                res?.status === 200
                  ? t("deletedSuccessfully")
                  : t("error"),
              subTitle:
                res?.msg || t("requestFailed"),
            });
          },

          (err) => {
            setLoader(false);

            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle:
                err?.msg ||
                err?.message ||
                t("requestFailed"),
            });
          }
        );

        _delete(
          endpoints.User,
          id,
          actions.ActionType.RmvList,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "firstName", label: t("firstName") },
    { id: "lastName", label: t("lastName") },
    { id: "userName", label: t("userName") },
    { id: "dob", label: t("dateOfBirth") },
    { id: "gender", label: t("gender") },
    { id: "phone", label: t("phone") },
    { id: "role", label: t("userRole") },
    { id: "isActive", label: t("activeStatus") },
    { id: "address", label: t("address") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("editThisRecord")}>
        <IconButton
          onClick={() => handleAddEdit(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("deleteThisRecord")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Add User Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addUser")}
    </Button>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("users")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("users")}
        />
      </Box>

      {/* Normal Users Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Users List */}
      <Box
        ref={reportRef}
        className="users-list-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("users")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* View User Details Dialog */}
      <Dialog
        show={showView}
        maxWidth="sm"
        onClose={handleCloseView}
      >
        {selectedUser && (
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("userDetails")}
              />
            </Box>

            <UserDetailsContent
              user={selectedUser}
              t={t}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
              }}
            >
              <Button
                onClick={handleCloseView}
                className="btn-error"
                size="large"
                sx={{ minWidth: "150px" }}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Printable User Details */}
      {selectedUser && (
        <Box
          ref={detailsRef}
          className="user-details-print"
          sx={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "1000px",
            backgroundColor: "#fff",
            color: "#000",
            p: 2,
          }}
        >
          <UserDetailsContent
            user={selectedUser}
            t={t}
          />
        </Box>
      )}

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(Users);
